import { action, computed, makeObservable, observable } from 'mobx';

import { askForUserLocationPermission, isGeolocationAvailable } from '../../utils/getUserLocation';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

const LOCATION_OPT_OUT_KEY = 'location_opt_out';

export class LocationStore {
  private readonly rootStore: RootStore;
  @observable
  public permissionState: PermissionState | null = null;

  @observable
  private appEnabled: boolean = localStorage.getItem(LOCATION_OPT_OUT_KEY) !== 'true';
  @observable public isInitialized: boolean = false;
  @observable public isDeviceLocationOff: boolean = false;
  private permissionStatus: PermissionStatus | null = null;
  @observable public isLocationRestrictedDialogShown: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
    void this.initialize();
  }

  public async refreshPermissionFromBrowser(): Promise<void> {
    if (!isGeolocationAvailable()) {
      return;
    }
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      this.setPermissionState(status.state);

      if (status.state === 'granted') {
        const isWorking = await askForUserLocationPermission();
        this.setDeviceLocationOff(!isWorking);
      } else {
        this.setDeviceLocationOff(false);
      }
    } catch {
      // Same as initialize: Permissions API unsupported or rejects (e.g. some Safari versions).
    }
  }

  @computed
  public get shouldShowLocationRestrictedDialog(): boolean {
    return (
      !this.isLocationRestrictedDialogShown &&
      this.rootStore.userStore.profile?.isPscAvailable === false &&
      this.rootStore.walletStore.wallet?.scCoin?.psc > 0
    );
  }

  @computed
  public get isBlocked(): boolean {
    return this.permissionState === 'denied';
  }

  @computed
  public get isEnabled(): boolean {
    return this.permissionState === 'granted' && this.appEnabled && !this.isDeviceLocationOff;
  }

  @action private setIsInitialized(value: boolean) {
    this.isInitialized = value;
  }

  @action
  private setDeviceLocationOff(value: boolean) {
    this.isDeviceLocationOff = value;
  }

  @action
  private setPermissionState(state: PermissionState) {
    this.permissionState = state;
  }

  @action
  private setAppEnabled(value: boolean) {
    this.appEnabled = value;
    localStorage.setItem(LOCATION_OPT_OUT_KEY, String(!value));
  }

  private async initialize(): Promise<void> {
    if (!isGeolocationAvailable()) {
      this.setIsInitialized(true);
      return;
    }

    try {
      this.permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
      this.setPermissionState(this.permissionStatus.state);

      this.permissionStatus.addEventListener('change', () => {
        if (this.permissionStatus) {
          this.setPermissionState(this.permissionStatus.state);
        }
      });
    } catch {
      // permissions API not supported
    } finally {
      this.setIsInitialized(true);
    }
  }

  public enableLocation = async (): Promise<void> => {
    if (this.isBlocked) {
      return;
    }
    const isGranted = await askForUserLocationPermission();
    if (this.permissionStatus) {
      this.setPermissionState(this.permissionStatus.state);
    }
    if (isGranted || this.permissionStatus?.state === 'granted') {
      this.setAppEnabled(true);
      this.setPermissionState('granted');
    }
  };

  public disableLocation = (): void => {
    this.setAppEnabled(false);
  };

  @action
  public showLocationRestrictedDialog = (): void => {
    this.isLocationRestrictedDialogShown = true;
    this.rootStore.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenLocationRestrictedDialog });
  };

  @action
  public resetStore = (): void => {
    this.isLocationRestrictedDialogShown = false;
  };
}
